// src/features/tasks/components/TaskForm.jsx
import React, { memo, useCallback, useState, useEffect } from 'react';
import {
  Stack,
  Alert,
} from '@mui/material';
import { useTaskCategories } from '../hooks/useTaskCategories';
import { useStaff } from '../../staff/hooks/useStaff';
import { useProcedures } from '../../procedures/hooks/useProcedures';
import BasicInformationSection from './TaskForm/BasicInformationSection';
import ExecutionSection from './TaskForm/ExecutionSection';
import RRuleSchedulingSection from './TaskForm/RRuleSchedulingSection';
import TaskAssignmentsSection from './TaskForm/TaskAssignmentsSection';
import TaskFormEquipmentSection from './TaskForm/TaskFormEquipmentSection';

const TaskForm = memo(function TaskForm({
  formData,
  onInputChange,
  validationErrors = {},
  loading = false,
  mode = 'create',
}) {
  const [staffMembers, setStaffMembers] = useState([]);
  const { categories, fetchTaskCategories } = useTaskCategories();
  const { fetchDepartmentStaff } = useStaff();
  const { procedures, fetchProcedures } = useProcedures();

  // Load data when component mounts
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchTaskCategories({ active_only: true }),
        loadStaffMembers(),
        fetchProcedures(false), // Only fetch active procedures
      ]);
    };
    loadData();
  }, [fetchTaskCategories, fetchProcedures]);

  const loadStaffMembers = useCallback(async () => {
    try {
      const result = await fetchDepartmentStaff({ include_inactive: false });
      if (result.success) {
        setStaffMembers(result.data.staff_members || []);
      }
    } catch (error) {
      console.error('Failed to load staff members:', error);
    }
  }, [fetchDepartmentStaff]);

  const handleEquipmentAdd = useCallback((items) => {
    const currentEquipment = formData.equipment || [];
    const newEntries = items.map((item) => ({
      equipment_id: item.equipment_id,
      relation_type: item.relation_type,
      notes: item.notes,
      rotation_label: item.rotation_label,
      rotation_options: item.rotation_options,
      ...item.equipment,
    }));
    onInputChange('equipment', [...currentEquipment, ...newEntries]);
  }, [formData.equipment, onInputChange]);

  const handleEquipmentRemove = useCallback((equipmentId) => {
    const currentEquipment = formData.equipment || [];
    const filtered = currentEquipment.filter((eq) => eq.equipment_id !== equipmentId);
    onInputChange('equipment', filtered);
  }, [formData.equipment, onInputChange]);

  return (
    <Stack spacing={5}>
      {/* Numbered so the long form reads as steps */}
      <BasicInformationSection
        number={1}
        formData={formData}
        onInputChange={onInputChange}
        validationErrors={validationErrors}
        loading={loading}
        categories={categories}
      />

      <ExecutionSection
        number={2}
        formData={formData}
        onInputChange={onInputChange}
        validationErrors={validationErrors}
        loading={loading}
        procedures={procedures}
        isCreateMode={mode === "create"}
      />

      <RRuleSchedulingSection
        number={3}
        formData={formData}
        onInputChange={onInputChange}
        validationErrors={validationErrors}
        loading={loading}
      />

      <TaskAssignmentsSection
        number={4}
        formData={formData}
        onInputChange={onInputChange}
        validationErrors={validationErrors}
        loading={loading}
        staffMembers={staffMembers}
      />

      {/* Equipment Section */}
      {mode === "create" && (
        <TaskFormEquipmentSection
          number={5}
          equipment={formData.equipment || []}
          onEquipmentAdd={handleEquipmentAdd}
          onEquipmentRemove={handleEquipmentRemove}
          loading={loading}
        />
      )}

    </Stack>
  );
});

export default TaskForm;